import React, { useState } from 'react';
import { ArrowUpRight, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import SystemMark from '../components/SystemMark';
import { SURFACE_CYCLE, TILT_CYCLE } from '../components/PlanetSurface';
import '../styles/NotFound.css';

/*
 * One status line per click, in order, then it repeats -- the joke is a
 * diagnostic log that never actually finds anything, in the same deadpan
 * voice the rest of the site's code comments use on themselves.
 */
const STATUS_LINES = [
  'Scanning known orbits for a page at this address...',
  'Checked the atlas index. This one is not filed anywhere.',
  'Pinged the forgetenders. They do not remember this one either.',
  'Ran kubectl get page --all-namespaces. Nothing scheduled.',
  "Consulted Goodhart's Law: the URL became a target, and stopped being a good measure.",
  'Rebooted the biplane. Still just banner quotes back there.',
  'This is either uncharted space, or someone mistyped a link.',
  'Recommend returning to a mapped world before the air runs out.',
];

const PATHS = [
  { to: '/', label: 'Home' },
  { to: '/in-practice', label: 'One year, nine hats' },
  { to: '/cultures', label: 'The nine cultures' },
  { to: '/career', label: 'Career atlas' },
  { to: '/resume', label: 'Full resume' },
  { to: '/homelab', label: 'Homelab field guide' },
];

const NotFound = () => {
  const [attempts, setAttempts] = useState(0);

  const recalibrate = () => setAttempts((n) => n + 1);
  const drifted = attempts % SURFACE_CYCLE.length;

  return (
    <div className="tmc-page page-shell notfound-page">
      <header className="tmc-page-header">
        <p className="eyebrow">Error 404</p>
        <h1>This coordinate is not in the atlas.</h1>
        <p className="tmc-lede">
          You have drifted past every orbit on the map. Recalibrate a few times if you
          like watching mission control fail politely, or just pick a world below.
        </p>
      </header>

      <div className="notfound-scene">
        <button
          type="button"
          className="notfound-planet"
          onClick={recalibrate}
          aria-label="Recalibrate the scan"
        >
          <SystemMark
            title="An uncharted world"
            coreRadius={54}
            surface={SURFACE_CYCLE[drifted]}
            tilt={TILT_CYCLE[drifted] + attempts * 11}
            color="var(--tmc-action-primary)"
            rings={[
              {
                tilt: -30 + attempts * 17,
                rx: 88,
                ry: 24,
                color: 'var(--tmc-signal-systems)',
                satellites: [{ angle: attempts * 53, color: 'var(--tmc-signal-systems)', r: 5 }],
              },
            ]}
          />
        </button>

        <div className="notfound-console">
          <p className="notfound-status">
            <span aria-hidden="true">&gt;</span> {STATUS_LINES[attempts % STATUS_LINES.length]}
          </p>
          <button type="button" className="tmc-btn" onClick={recalibrate}>
            <RotateCcw size={16} aria-hidden="true" /> Recalibrate
          </button>
          <p className="notfound-attempts">
            {attempts === 0 ? 'No attempts yet' : `Attempts: ${attempts}`}
          </p>
        </div>
      </div>

      <nav className="notfound-paths" aria-label="Known worlds">
        {PATHS.map((path) => (
          <Link to={path.to} key={path.to}>
            <strong>{path.label}</strong>
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default NotFound;
